import { CoreEvent, eventNames, type Events } from '@carvana/core-events';
import {
  useEffectWithController,
  usePromise
} from '@carvana/merch-react-hooks';
import { resetNotificationForViewedSearches } from '@carvana/merch-search-bar/utils/savedSearchNotifications';
import { useContext, useEffect } from 'react';
import SavedSearchesContext from '~/context/common/SavedSearchesContext';
import UserContext, {
  useIsLoggedIn,
  useUserId
} from '~/context/common/UserContext';
import { readSavedSearch } from '~/services/common/savedSearch';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetSavedSearches(onUpdateCallback?: () => void) {
  const {
    fetchSavedSearchTrigger,
    savedSearches: { setLoading, setData, setLoaded, setError }
  } = useContext(SavedSearchesContext);
  const mounted = usePromise();
  const {
    userState: { hasEverLoaded, isLoaded },
    location: {
      data: { zip }
    }
  } = useContext(UserContext);
  const isLoggedIn = useIsLoggedIn();
  const userId = useUserId();

  useEffect(() => {
    function updateSavedSearchData(
      payload: Events[typeof eventNames.favorites.mfe.refresh]
    ) {
      if (payload.source === 'app' || !payload.searches) return;

      setData(payload.searches.reverse());
      onUpdateCallback?.();
    }

    CoreEvent.on(eventNames.favorites.mfe.refresh, updateSavedSearchData);

    return () => {
      CoreEvent.off(eventNames.favorites.mfe.refresh, updateSavedSearchData);
    };
  }, [onUpdateCallback, setData]);

  useEffectWithController(
    controller => {
      if (!isLoaded || !isLoggedIn) {
        if (hasEverLoaded) {
          setLoaded();
        }

        return;
      }

      setLoading();

      const query = new URLSearchParams({
        browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
        userId,
        zip5: zip
      }).toString();

      mounted(readSavedSearch(query, controller))
        .then(({ data }) => {
          const searches = resetNotificationForViewedSearches(data.reverse());
          setData(searches);
        })
        .catch(
          generateErrorLogger(
            'Get saved searches',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      fetchSavedSearchTrigger,
      hasEverLoaded,
      isLoaded,
      isLoggedIn,
      mounted,
      setData,
      setError,
      setLoaded,
      setLoading,
      userId,
      zip
    ]
  );
}
